<template lang='pug'>
  .project-view
    err404.main-container-wide(v-if="error")
    .gym(v-else)
      Drawer(v-if="settingsDrawer" @hide="settingsDrawer = false" :title="__('Sharing Settings')")
        gym-settings-drawer(:project="project")
      zeus-header(ref="zeus" :project="project")
      DynamicMedia.hero.main-container-fluid(:config="activeRender")
        render-loading(v-if="activeRender && activeRender.pending")
      .main-container-wide
        .additional-renders.custom-scroll(v-if="project.renders.length > 1" v-drag-scroll)
          DynamicMedia.render(
            @click="activeRender = render"
            v-for="render in project.renders"
            :key="render.src"
            :class="{active: activeRender === render}"
            :config="render"
          )
            render-loading(v-if="render.pending")
        .name-row
          h2.underline(:class="{loading: loading}") {{ project.name || 'Gym' }}
          .buttons
            RogueButton(size='s' @click="router.push(`/zeus-builder`)") {{ __('Return to Dashboard', 'zeus') }}
            RogueButton.mobile-view-layout(size='s' :link="`${projectLink}&readonly=true`")
              | {{__('View Layout') }}
            RogueButton.edit-layout(size='s' :link="projectLink")
              | {{ project.canEdit ? __('Edit Layout', 'zeus') : __('Create a Copy', 'zeus') }}
            RogueButton(size='s' @click="settingsDrawer = true" v-if="project.canEdit") {{ __('Share Settings', 'zeus') }}
        p.blurb(v-if="project.canEdit") {{ __('To change or remove products from this list, edit your layout.', 'zeus') }}
        .grid(v-if="loading || (validBuyRequests.length)")
          .products
            product-card(
              v-if="loading"
              v-for="(key) in 4"
              :key="key"
            )
            product-card(
              v-if="!loading"
              v-for="(buyRequest, i) in validBuyRequests"
              :key="buyRequest.productId + '' + i"
              :buyRequest="buyRequest"
            )
          .totals
            .gray-bg
              h3 {{ __('Build Summary', 'zeus') }}
              .qty
                .left {{ __('Quantity', 'zeus') }}
                b.right {{ __('{count} Products', {count: totalQty}) }}
              .subtotal
                b.left {{ __('Subtotal') }}
                b.right {{ rogue.currency(subtotal ?? 0) }}
            RogueButton(color="red" @click="addAll()" width="full") {{__('Add All To Cart')}}
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useListStore } from '@/js/stores/listStore'
import { useProductStore } from '@/js/stores/productStore'
import { computePriceSync } from '@/js/helpers/price-utils'
import ProductCard from '@/js/pages/gym-builder/components/product-card.vue'
import stockStatus from '@/js/helpers/stockStatus'
import { useRogue } from '@/js/services/Rogue'
import type { BuyRequest } from '@/js/services/types/order'
import zeusHeader from '../components/header.vue'
import gymSettingsDrawer from '../components/gym-settings-drawer.vue'
import renderLoading from '../components/render-loading.vue'
import type { GymProject, GymRender } from '../types'

const route = useRoute()
const router = useRouter()
const rogue = useRogue()
const listStore = useListStore()
const productStore = useProductStore()

const loading = ref(true)
const totalQty = ref<number | null>(null)
const subtotal = ref<number | null>(null)
const error = ref(false)
const settingsDrawer = ref(false)
const activeRender = ref<GymRender | undefined>({ width: 1600, height: 900, src: '/Gym%20Builder/placeholder.jpg' })

const project = ref<GymProject>({
  _id         : null,
  renders     : [],
  buyRequests : [],
  name        : null,
  canEdit     : false,
})
const validBuyRequests = ref<BuyRequest[]>([])

let pollTimeout: ReturnType<typeof setTimeout> | undefined

const products = computed(() => productStore.products)
const projectLink = computed(() => `/zeus-builder/project/edit?${project.value.canEdit ? 'projectId=' : 'templateId='}${project.value._id}`)

async function load() {
  // Load the project
  try {
    const loaded = await rogue.fetch<GymProject>(`/svc_gym/projects/${route.params.projectId}`)
    project.value = loaded
    validBuyRequests.value = []
    totalQty.value = 0

    loaded.buyRequests.forEach(async (buyRequest) => {
      try {
        const toLoad = [buyRequest.productId, buyRequest.metadata?.groupedProductId, ...(buyRequest.children?.map(child => child.productId) || [])]
        await productStore.loadAll(toLoad.filter(id => Boolean(id)) as number[], rogue)
        const price = computePriceSync(buyRequest, rogue.pinia!)
        if (price) {
          totalQty.value = (totalQty.value ?? 0) + buyRequest.qty
          subtotal.value = (subtotal.value ?? 0) + price
          validBuyRequests.value.push(buyRequest)
        }
      } catch (e) {
        rogue.log.warn('Error creating BuyRequest in Gym Viewer')
      }
    })

    loading.value = false

    activeRender.value = project.value.renders.find(r => r.renderId === activeRender.value?.renderId) || project.value.renders[0]
    if (project.value.renders.some(r => r.pending)) {
      pollTimeout = setTimeout(() => load(), 5000)
    }
  } catch (e) {
    error.value = true
    loading.value = false
  }
}

function addAll() {
  // Find in stock buy requests and create clones of them
  const inStock: BuyRequest[] = validBuyRequests.value
    .filter(br => stockStatus.forBuyRequest(br, products.value) === '')
    .map(br => JSON.parse(JSON.stringify(br)))

  // Remove the extra gym metadata
  inStock.forEach(buyRequest => delete buyRequest.metadata?.gym)
  listStore.addListItems(rogue, 'cart', inStock)
}

onMounted(() => {
  load()
})

onBeforeUnmount(() => {
  clearTimeout(pollTimeout)
})
</script>

<style lang='scss' scoped>
.project-view {
  padding-bottom: $space-l;
}

.hero {
  width: 100%;
  margin-bottom: $space-m;
}

.name-row {
  position: relative;

  @include not-mobile() {
    .buttons {
      position: absolute;
      top: 0;
      right: 0;
    }

    .button {
      display: inline-block;
      margin-left: $space-s;
      padding: $space-xxxs $space-xxs;
    }
  }

  @include mobile() {
    .buttons .button {
      margin-left: 0;
      width: 100%;
      margin-bottom: $space-xs;
    }
  }

  h2.loading {
    color: $color-white;
  }

}

.blurb {
  font-size: $size-xs;
}

.grid {
  @include all-desktop() {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
  }
}

.products {
  display: grid;
  flex-grow: 1;
  gap: $space-xs;
  grid-template-columns: 100%;
  grid-auto-rows: auto;
  margin-bottom: $space-s;

  @include not-mobile() {
    grid-template-columns: 50% 50%;
  }
}

.additional-renders {
  display: flex;
  margin-top: $space-s;
  overflow-x: auto;
  padding-bottom: $space-s;
  margin-bottom: $space-s;

  .render {
    height: 173px;
    cursor: pointer;
    width: 308px;
    flex-shrink: 0;
    margin-left: $space-s;
    border: 1px solid $color-gray-x-light;

    @include mobile() {
      width: 200px;
      height: 112px;
    }

    &.active {
      border-color: $color-black-light;
    }

    &:first-child {
      margin-left: 0;
    }
  }
}

.button.edit-layout {
  display: none;
}

@include not-mobile() {
  .button.mobile-view-layout {
    display: none;
  }

  .button.edit-layout {
    display: inline-block;
  }
}

.totals {
  position: sticky;
  top: $space-s;
  flex-basis: 320px;
  flex-grow: 0;
  flex-shrink: 0;
  margin-bottom: $space-s;

  .gray-bg {
    background-color: $color-gray-x-light;
    padding: $space-s;
    margin-bottom: $space-s;
  }

  @include all-desktop() {
    margin-bottom: 0;
    margin-left: $space-m;
  }

  .qty, .subtotal {
    background-color: $color-white;
    display: flex;
    padding: $space-s;

    .left, .right {
      flex-grow: 1;
    }

    .right {
      text-align: right;
    }
  }

  .qty {
    font-size: $size-xs;
    border-bottom: 1px solid $color-gray-light;
  }

  .subtotal {
    font-size: $size-s;
  }
}
</style>
